import React, { useState, useEffect, useMemo } from 'react'
import { Modal, Table, Tag, Spin, Button, Input, message } from 'antd'
import { SearchOutlined, DownloadOutlined } from '@ant-design/icons'
import styled from 'styled-components'
import moment from 'moment'
import { API } from 'src/utils/interceptor'

const LostProductsModal = ({ visible, onClose, venue, supplierIds = [] }) => {
  const [isLoading, setIsLoading] = useState(false)
  const [lostProducts, setLostProducts] = useState([])
  const [searchTerm, setSearchTerm] = useState('')
  const [error, setError] = useState(false)

  const venueName =
    venue?.venueTitle || venue?.title || venue?.name || 'this customer'

  // Fetch lost products details from backend
  useEffect(() => {
    if (!visible || !venue?._id || supplierIds.length === 0) {
      setLostProducts([])
      setIsLoading(false)
      setSearchTerm('')
      setError(false)
      return
    }

    let isCancelled = false

    const fetchLostProductsDetails = async () => {
      setIsLoading(true)
      setError(false)
      setLostProducts([])

      try {
        const venueId = venue._id || venue.venueId
        const response = await API.post(
          'api2',
          'venues/lost-products/details',
          {
            body: { venueId, supplierIds },
          },
        )

        if (!isCancelled) {
          if (response?.success && Array.isArray(response?.data)) {
            setLostProducts(response.data)
            setError(false)
          } else {
            throw new Error('Invalid response format')
          }
        }
      } catch (err) {
        console.error('Failed to fetch lost products details:', err)
        if (!isCancelled) {
          setError(true)
          setLostProducts([])
          message.error('Unable to load lost products. Please try again.')
        }
      } finally {
        if (!isCancelled) {
          setIsLoading(false)
        }
      }
    }

    fetchLostProductsDetails()

    return () => {
      isCancelled = true
    }
  }, [visible, venue?._id, supplierIds])

  // Filter products by search term (name or SKU)
  const filteredProducts = useMemo(() => {
    if (!searchTerm.trim()) {
      return lostProducts
    }
    const term = searchTerm.toLowerCase().trim()
    return lostProducts.filter(
      product =>
        (product.productName || '').toLowerCase().includes(term) ||
        (product.productSku || '').toLowerCase().includes(term),
    )
  }, [lostProducts, searchTerm])

  const formatCurrencyFromPence = value => {
    if (!Number.isFinite(value)) {
      return '—'
    }
    return `£${Number(value).toLocaleString('en-GB', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    })}`
  }

  const formatLastOrderDate = dateValue => {
    if (!dateValue) return '—'
    const parsed = moment(dateValue)
    if (!parsed.isValid()) return '—'
    return parsed.format('DD/MM/YY')
  }

  const handleDownloadCSV = () => {
    if (filteredProducts.length === 0) {
      message.warning('No data to download')
      return
    }

    // Prepare CSV data
    const headers = [
      'Product Name',
      'SKU',
      'Type',
      'Last Ordered',
      'Qty (monthly average)',
      'Value (monthly average)',
    ]

    const csvRows = [
      headers.join(','),
      ...filteredProducts.map(product => {
        // Use the same value as displayed in the modal (formatCurrencyFromPence handles pence to pounds conversion)
        const row = [
          `"${(product.productName || '').replace(/"/g, '""')}"`,
          `"${(product.productSku || '—').replace(/"/g, '""')}"`,
          `"${(product.type || '').replace(/"/g, '""')}"`,
          `"${formatLastOrderDate(product.lastOrderedDate)}"`,
          product.quantity30DaysBefore || 0,
          `"${formatCurrencyFromPence(product.value30DaysBefore)}"`,
        ]
        return row.join(',')
      }),
    ]

    const csvContent = csvRows.join('\n')
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' })
    const link = document.createElement('a')
    const url = URL.createObjectURL(blob)

    link.setAttribute('href', url)
    link.setAttribute(
      'download',
      `lost-products-${venueName
        .replace(/[^a-z0-9]/gi, '-')
        .toLowerCase()}-${moment().format('YYYY-MM-DD')}.csv`,
    )
    link.style.visibility = 'hidden'
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
    URL.revokeObjectURL(url)

    message.success('CSV downloaded successfully')
  }

  const columns = useMemo(
    () => [
      {
        title: 'Product Name',
        dataIndex: 'productName',
        key: 'productName',
        sorter: (a, b) =>
          String(a.productName || '').localeCompare(
            String(b.productName || ''),
          ),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'SKU',
        dataIndex: 'productSku',
        key: 'productSku',
        render: sku => <span style={{ color: '#111827' }}>{sku || '—'}</span>,
        sorter: (a, b) =>
          String(a.productSku || '').localeCompare(String(b.productSku || '')),
        sortDirections: ['ascend', 'descend'],
      },
      {
        title: 'Type',
        dataIndex: 'type',
        key: 'type',
      },
      {
        title: 'Last Ordered',
        dataIndex: 'lastOrderedDate',
        key: 'lastOrderedDate',
        render: date => (
          <span style={{ color: '#111827' }}>{formatLastOrderDate(date)}</span>
        ),
        sorter: (a, b) => {
          const aDate = a.lastOrderedDate
            ? new Date(a.lastOrderedDate).getTime()
            : 0
          const bDate = b.lastOrderedDate
            ? new Date(b.lastOrderedDate).getTime()
            : 0
          return aDate - bDate
        },
        sortDirections: ['descend', 'ascend'],
        defaultSortOrder: 'descend',
      },
      {
        title: 'Qty (monthly average)',
        dataIndex: 'quantity30DaysBefore',
        key: 'quantity30DaysBefore',
        width: 160,
        render: qty => (
          <span style={{ color: '#111827' }}>
            {Number.isFinite(qty) ? qty : '—'}
          </span>
        ),
        sorter: (a, b) => {
          const aQty = Number.isFinite(a.quantity30DaysBefore)
            ? a.quantity30DaysBefore
            : 0
          const bQty = Number.isFinite(b.quantity30DaysBefore)
            ? b.quantity30DaysBefore
            : 0
          return aQty - bQty
        },
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: 'Value (monthly average)',
        dataIndex: 'value30DaysBefore',
        key: 'value30DaysBefore',
        render: value => (
          <span style={{ color: '#111827' }}>
            {formatCurrencyFromPence(value)}
          </span>
        ),
        sorter: (a, b) => {
          const aValue = Number.isFinite(a.value30DaysBefore)
            ? a.value30DaysBefore
            : 0
          const bValue = Number.isFinite(b.value30DaysBefore)
            ? b.value30DaysBefore
            : 0
          return aValue - bValue
        },
        sortDirections: ['descend', 'ascend'],
      },
      {
        title: '',
        key: 'badge',
        width: 140,
        render: () => (
          <Tag color='#dc2626' style={{ margin: 0 }}>
            Potentially Lost
          </Tag>
        ),
      },
    ],
    [],
  )

  return (
    <Modal
      visible={visible}
      onCancel={onClose}
      footer={null}
      width={900}
      destroyOnClose
      title={null}
    >
      <ModalBody>
        <ModalTitle>Lost Products of {venueName}</ModalTitle>
        <ModalDescription>
          These products were previously ordered by {venueName} but have not
          been ordered in the last 30 days.
        </ModalDescription>

        {isLoading ? (
          <LoadingState>
            <Spin />
            <LoadingText>Sniffing out missing orders…</LoadingText>
          </LoadingState>
        ) : error ? (
          <EmptyState>
            <EmptyStateTitle>Unable to load lost products</EmptyStateTitle>
            <EmptyStateText>
              There was an error loading the lost products data. Please try
              again later.
            </EmptyStateText>
          </EmptyState>
        ) : lostProducts.length === 0 ? (
          <EmptyState>
            <EmptyStateTitle>
              Everything looks good — no lost products.
            </EmptyStateTitle>
            <EmptyStateText>
              All products that were previously ordered are still being ordered
              regularly.
            </EmptyStateText>
          </EmptyState>
        ) : (
          <>
            <SearchAndDownloadWrapper>
              <SearchWrapper>
                <Input
                  placeholder='Search by product name or SKU...'
                  prefix={<SearchOutlined />}
                  value={searchTerm}
                  onChange={e => setSearchTerm(e.target.value)}
                  allowClear
                />
              </SearchWrapper>
              <DownloadButton
                type='default'
                icon={<DownloadOutlined />}
                onClick={handleDownloadCSV}
              >
                Download CSV
              </DownloadButton>
            </SearchAndDownloadWrapper>
            <TableWrapper>
              <Table
                dataSource={filteredProducts}
                columns={columns}
                rowKey='productId'
                pagination={false}
                scroll={{ x: true }}
                locale={{
                  emptyText:
                    filteredProducts.length === 0 && searchTerm.trim() ? (
                      <EmptyState>
                        <EmptyStateTitle>No products found</EmptyStateTitle>
                        <EmptyStateText>
                          No products match &quot;{searchTerm}&quot;
                        </EmptyStateText>
                      </EmptyState>
                    ) : null,
                }}
              />
            </TableWrapper>
          </>
        )}

        {!isLoading && (
          <ModalFooter>
            <Button onClick={onClose}>Close</Button>
          </ModalFooter>
        )}
      </ModalBody>
    </Modal>
  )
}

const ModalBody = styled.div`
  padding: 8px 4px 16px;
`

const ModalTitle = styled.h3`
  margin: 0;
  font-size: 22px;
  font-weight: 700;
`

const ModalDescription = styled.p`
  margin: 8px 0 24px 0;
  color: #6b7280;
`

const SearchAndDownloadWrapper = styled.div`
  display: flex;
  gap: 12px;
  margin-bottom: 16px;
  align-items: center;

  @media (max-width: 768px) {
    flex-direction: column;
    align-items: stretch;
  }
`

const SearchWrapper = styled.div`
  flex: 1;

  .ant-input {
    border-radius: 8px;
  }
`

const DownloadButton = styled(Button)`
  white-space: nowrap;
  border-radius: 8px;
`

const LoadingState = styled.div`
  display: flex;
  align-items: center;
  gap: 12px;
  color: #6b7280;
  padding: 32px 16px;
  justify-content: center;
`

const LoadingText = styled.span`
  font-size: 15px;
  color: #6b7280;
`

const EmptyState = styled.div`
  border: 1px dashed #d1d5db;
  border-radius: 12px;
  padding: 48px 24px;
  text-align: center;
  color: #6b7280;
  display: flex;
  flex-direction: column;
  gap: 8px;
`

const EmptyStateTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  color: #111827;
  margin-bottom: 4px;
`

const EmptyStateText = styled.div`
  font-size: 14px;
  color: #6b7280;
`

const TableWrapper = styled.div`
  margin-top: 8px;

  .ant-table-thead > tr > th {
    background: #f7fafc;
    border-bottom: 1px solid #e2e8f0;
    font-weight: 600;
    color: #0f172a;
  }

  .ant-table-tbody > tr > td {
    border-bottom: 1px solid #e2e8f0;
  }

  .ant-table-tbody > tr:hover {
    background: #f7fafc;
  }
`

const ModalFooter = styled.div`
  display: flex;
  justify-content: flex-end;
  margin-top: 24px;
  padding-top: 16px;
  border-top: 1px solid #e5e7eb;
`

export default LostProductsModal
